import { SettlementChances } from "../../shared/settlementProspects";
import { PartyAgreements } from "../../shared/Agreements.ts";
import { CaseCounts } from "../../shared/CaseCounts.ts";
import {
  BookedReserve,
  ClaimKind,
  Case,
  CaseParameters,
  Claim,
  Damage,
  Configuration,
  CostStructure,
  Party,
  Results,
  SettlementOffer,
} from "../../shared/CaseModel";
import { Timeline } from "../../shared/timeline";
import { CaseListElement } from "../../shared/CasesList.ts";
import { Budget } from "../../shared/CostModel";
import { CaseFilter, QueryOptions } from "../../shared/parse/CasesQuery.ts";
import {
  PortfolioOverview,
  PortfolioStats,
  SettlementPortfolio,
} from "../../shared/PortfolioTypes.ts";
import randomId from "../../shared/randomId";
import { CaseApi } from "./CaseApi";
import { DecisionTreeResult } from "./decisionTrees.ts";

export class BudgetInitializingCaseApiDecorator implements CaseApi {
  constructor(private readonly api: CaseApi) {}

  getCasesList(options: QueryOptions): Promise<CaseListElement[]> {
    return this.api.getCasesList(options);
  }

  queryAccessNames(): Promise<string[]> {
    return this.api.queryAccessNames();
  }

  getCaseCounts(filter?: CaseFilter): Promise<CaseCounts> {
    return this.api.getCaseCounts(filter);
  }

  getPortfolioStats(filter?: CaseFilter): Promise<PortfolioStats> {
    return this.api.getPortfolioStats(filter);
  }

  getSettlementPortfolio(filter?: CaseFilter): Promise<SettlementPortfolio> {
    return this.api.getSettlementPortfolio(filter);
  }

  getPortfolioOverview(): Promise<PortfolioOverview> {
    return this.api.getPortfolioOverview();
  }

  queryCase(caseId: string): Promise<Case> {
    return this.api.queryCase(caseId);
  }

  queryVariantResults(
    caseId: string,
  ): Promise<{ [variantId: string]: Results }> {
    return this.api.queryVariantResults(caseId);
  }

  generateDecisionTrees(
    caseId: string,
    onProgress: (share: number) => void,
    signal: AbortSignal,
  ): Promise<DecisionTreeResult> {
    return this.api.generateDecisionTrees(caseId, onProgress, signal);
  }

  addCase(c: Case): Promise<Case> {
    return this.api.addCase(c);
  }

  importCase(
    config: CaseParameters,
    options: { overwriteId?: string },
  ): Promise<Case> {
    return this.api.importCase(config, options);
  }

  removeCase(caseId: string): Promise<void> {
    return this.api.removeCase(caseId);
  }

  closeStage(
    caseId: string,
    timeline: Timeline,
    budgets: Budget[],
    record?: Pick<Case, "judgment" | "closing">,
  ): Promise<void> {
    return this.api.closeStage(caseId, timeline, budgets, record);
  }

  updateBookedReserves(
    caseId: string,
    bookedReserves: BookedReserve[],
  ): Promise<void> {
    return this.api.updateBookedReserves(caseId, bookedReserves);
  }

  updateTimeline(caseId: string, timeline: Timeline): Promise<void> {
    return this.api.updateTimeline(caseId, timeline);
  }

  setTitle(
    caseId: string,
    title: string,
    claimKind?: ClaimKind,
  ): Promise<void> {
    return this.api.setTitle(caseId, title, claimKind);
  }

  addParty(caseId: string, party: Party): Promise<Party[]> {
    // initialize cost recoveries
    if (party.recoveries === undefined || party.recoveries.length === 0)
      party.recoveries = [
        {
          id: randomId(),
          type: "barristerFee",
          percentage: 0,
        },
        {
          id: randomId(),
          type: "courtFee",
          percentage: 0,
        },
        {
          id: randomId(),
          type: "expertFee",
          percentage: 0,
        },
        {
          id: randomId(),
          type: "lawFirmFee",
          percentage: 0,
        },
        {
          id: randomId(),
          type: "otherDisbursement",
          percentage: 0,
        },
      ];

    return this.api.addParty(caseId, party);
  }

  updateParty(caseId: string, prototype: Party): Promise<void> {
    return this.api.updateParty(caseId, prototype);
  }

  updateApportionment(
    caseId: string,
    party: Party,
    budget: Budget,
  ): Promise<void> {
    return this.api.updateApportionment(caseId, party, budget);
  }

  removeParty(caseId: string, partyId: string): Promise<void> {
    return this.api.removeParty(caseId, partyId).then(() =>
      // clean-up budgets
      this.api.removeBudget(caseId, partyId),
    );
  }

  addClaim(caseId: string, claim: Claim): Promise<Claim[]> {
    return this.api.addClaim(caseId, claim);
  }

  updateClaim(caseId: string, claim: Claim, damage?: Damage): Promise<void> {
    return this.api.updateClaim(caseId, claim, damage);
  }

  removeClaim(caseId: string, claimId: string): Promise<void> {
    return this.api.removeClaim(caseId, claimId);
  }

  addBudget(caseId: string, budget: Budget): Promise<Budget[]> {
    return this.api.addBudget(caseId, budget);
  }

  updateBudget(caseId: string, budget: Budget): Promise<void> {
    return this.api.updateBudget(caseId, budget);
  }

  removeBudget(caseId: string, partyId: string): Promise<void> {
    return this.api.removeBudget(caseId, partyId);
  }

  updateSettlementChances(
    caseId: string,
    settlementChances: SettlementChances[],
  ): Promise<void> {
    return this.api.updateSettlementChances(caseId, settlementChances);
  }

  addOffer(caseId: string, offer: SettlementOffer): Promise<SettlementOffer[]> {
    return this.api.addOffer(caseId, offer);
  }

  updateOffer(caseId: string, offer: SettlementOffer): Promise<void> {
    return this.api.updateOffer(caseId, offer);
  }

  removeOffer(caseId: string, offerId: string): Promise<void> {
    return this.api.removeOffer(caseId, offerId);
  }

  setBargainingVariant(caseId: string, variant?: Configuration): Promise<void> {
    return this.api.setBargainingVariant(caseId, variant);
  }

  updateCostStructure(caseId: string, structure: CostStructure): Promise<void> {
    return this.api.updateCostStructure(caseId, structure);
  }
  setPartyAgreements(
    caseId: string,
    partyAgreements: PartyAgreements,
  ): Promise<void> {
    return this.api.setPartyAgreements(caseId, partyAgreements);
  }
}
